'use strict';

/**
 * Accessibility layer for the auto-rotating promo banners in the header: the Page
 * Designer `FTCampaignBanner` component and the `global-campaign-banners` content
 * asset. Both run tiny-slider over slides whose markup is authored in Business
 * Manager, so the remediation has to be applied to the rendered DOM rather than to
 * a template.
 *
 * WCAG 2.1.1 (Keyboard)
 *   Only the slide actually on screen keeps its controls in the tab order. Every
 *   other slide - originals and tiny-slider clones alike - is marked `inert`, which
 *   blocks focus even when an overlay re-applies `tabindex="0"` after load. Because
 *   a clone is the visible slide while the loop wraps, the hidden/visible split is
 *   recalculated from tiny-slider's own state on every transition instead of being
 *   pinned to the cloned class. Autoplay pauses while focus is inside the banner so
 *   the link cannot rotate away from under the keyboard user.
 *
 * WCAG 2.4.4 (Link Purpose)
 *   Generic link text such as "View Details" is given an accessible name built from
 *   the message of its own slide, e.g. "View Details for 20% Off All Suits".
 *
 * WCAG 2.2.2 (Pause, Stop, Hide)
 *   A rotating banner gets a labelled Previous / Play-Pause / Next control group.
 *   The pause is sticky - it outlives tabbing away, a slide change, and the hover
 *   and focus holds - so the shopper, not a timer, decides when the banner moves.
 *   A banner with a single slide never rotates and gets no controls.
 */

var SLIDE = '.tns-item';
// `a[href]` rather than `a` so a bare in-page anchor never becomes a phantom tab stop.
var FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex], [role="link"], [role="button"]';
var CONTROL = 'a, button, [role="link"], [role="button"]';
// Plus anything the content author wired up with script instead of an href.
var CLICKABLE = CONTROL + ', [onclick], [data-href]';
// Controls that the browser does not operate with Enter/Space on their own.
var NON_NATIVE_CONTROL = '[role="link"]:not(a[href]), [role="button"]:not(button):not(input)';
var DEFAULT_CONNECTOR = 'for';
// Tags the browser already makes focusable and operable with Enter/Space.
var NATIVE_CONTROL_TAG = { A: true, BUTTON: true, INPUT: true, SELECT: true, TEXTAREA: true };

// Link text that says nothing about where the link goes.
var GENERIC_LINK_TEXT = [
    'view details', 'details', 'see details', 'view detail', 'view',
    'shop now', 'shop', 'buy now', 'order now',
    'learn more', 'read more', 'see more', 'view more', 'more', 'more info',
    'more information', 'click here', 'here', 'go', 'get started', 'find out more'
];

/**
 * Collapse whitespace and trim.
 * @param {string} text - raw text
 * @returns {string} normalized text
 */
function normalize(text) {
    return (text || '').replace(/\s+/g, ' ').trim();
}

/**
 * True when the link text gives the user no idea of the destination.
 * @param {string} text - the link's visible text
 * @returns {boolean} whether the text is generic
 */
function isGeneric(text) {
    var stripped = normalize(text).toLowerCase().replace(/[\s.:>›»→>]+$/, '');
    return !stripped || GENERIC_LINK_TEXT.indexOf(stripped) > -1;
}

/**
 * The slide's message with the text of its own controls removed, so it can be used
 * as the context for a generic link.
 * @param {jQuery} $slide - the slide element
 * @returns {string} the surrounding message
 */
function getSlideMessage($slide) {
    var $copy = $slide.clone();
    $copy.find(CLICKABLE).remove();
    return normalize($copy.text());
}

/**
 * Give every generic control in the banner an accessible name that names its
 * destination, and make sure non-native controls expose a role and are reachable.
 * Runs once per slider; clones are decorated too because a clone is the visible
 * slide while the loop wraps.
 * @param {jQuery} $track - the tiny-slider container
 * @param {string} connector - word joining link text and slide message ("for")
 */
function decorateControls($track, connector) {
    var usedLabels = {};

    $track.find(SLIDE).each(function (slideIndex) {
        var $slide = $(this);
        var message = getSlideMessage($slide);

        $slide.find(CLICKABLE).each(function () {
            var $control = $(this);
            var scriptedHref = $control.attr('data-href') || $control.attr('data-url');
            var isNative = NATIVE_CONTROL_TAG[this.tagName] === true
                && (this.tagName !== 'A' || !!$control.attr('href'));

            // A control the author wired up with script carries neither a role nor a
            // tab stop of its own; give it both so it behaves like what it looks like.
            if (!isNative && !$control.attr('role')) {
                $control.attr('role', scriptedHref ? 'link' : 'button');
            }

            if ($control.data('a11yLabelled')) {
                return;
            }
            $control.data('a11yLabelled', true);

            var text = normalize($control.text());
            if ($control.attr('aria-label') || !isGeneric(text) || !message) {
                return;
            }

            var label = text ? text + ' ' + connector + ' ' + message : message;

            // Links that go to the same place keep the same name; a collision
            // between different destinations gets its slide position appended so
            // every name in the banner stays unique.
            var href = $control.attr('href') || scriptedHref || '';
            if (Object.prototype.hasOwnProperty.call(usedLabels, label) && usedLabels[label] !== href) {
                label += ' (' + (slideIndex + 1) + ')';
            } else {
                usedLabels[label] = href;
            }

            $control.attr('aria-label', label);
        });
    });
}

/**
 * Put a slide in or out of the tab order and the accessibility tree.
 * @param {jQuery} $slide - the slide element
 * @param {boolean} hidden - whether the slide is off screen
 */
function setSlideHidden($slide, hidden) {
    if (hidden) {
        $slide.attr('aria-hidden', 'true');
        $slide.attr('inert', '');
        $slide.find(FOCUSABLE).attr('tabindex', '-1');
    } else {
        $slide.removeAttr('inert');
        $slide.removeAttr('aria-hidden');
        $slide.removeAttr('tabindex');
        $slide.find(FOCUSABLE).attr('tabindex', '0');
    }
}

/**
 * Re-derive which slide is on screen and move the tab order with it.
 *
 * The on-screen range comes from tiny-slider's index, NOT from its
 * `tns-slide-active` class. On a vertical axis tiny-slider defers initTools() -
 * the only thing that adds that class - until after images resolve, so `tns()`
 * returns with the class on no slide at all. Keying off it made every slide look
 * off screen and put `inert` on the whole banner: nothing focusable, nothing
 * clickable, nothing for a screen reader, and on a single-slide banner (frozen,
 * so no transition ever fires) it never recovered. The index is correct from the
 * moment `tns()` returns; anything unexpected fails open.
 *
 * @param {Object} slider - the tiny-slider instance
 * @param {jQuery} $track - the tiny-slider container
 */
function syncFocusability(slider, $track) {
    var $slides = $track.children(SLIDE);
    if (!$slides.length) {
        return;
    }

    var info = slider.getInfo();
    var first = info.index;
    var last = first + (info.items > 0 ? info.items : 1) - 1;

    // Fail open: never let a surprising index take the entire banner out of reach.
    if (typeof first !== 'number' || first < 0 || first >= $slides.length) {
        $slides.each(function () {
            setSlideHidden($(this), false);
        });
        return;
    }

    $slides.each(function (i) {
        setSlideHidden($(this), i < first || i > last);
    });
}

/**
 * Enter/Space activation for controls the browser will not activate itself.
 * Delegated so it survives tiny-slider moving slides around.
 * @param {jQuery} $track - the tiny-slider container
 */
function bindKeyboardActivation($track) {
    $track.on('keydown', NON_NATIVE_CONTROL, function (event) {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault();
            this.click();
        }
    });
}


// --- Playback controls (WCAG 2.2.2) -----------------------------------------

var CONTROLS_CLASS = 'banner-carousel-controls';
var CONTROLS_FLAG_CLASS = 'has-banner-carousel-controls';
var BTN_CLASS = 'banner-carousel-controls__btn';

var DEFAULT_LABELS = {
    group: 'Promotional message controls',
    pause: 'Pause promotional messages',
    play: 'Play promotional messages',
    previous: 'Previous promotional message',
    next: 'Next promotional message'
};

// Inline so the control group works wherever the banner is rendered, without
// depending on a sprite the Page Designer component does not load. `currentColor`
// leaves the colour to the stylesheet, which sets it per banner background.
var ICONS = {
    previous: '<path d="M2 9L7 4l5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
    next: '<path d="M2 5l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
    pause: '<path d="M3 2h3v10H3zm5 0h3v10H8z" fill="currentColor"/>',
    play: '<path d="M4 2l8 5-8 5z" fill="currentColor"/>'
};

var trackIdSeed = 0;

/**
 * Wrap an icon path in an SVG that is invisible to assistive tech - the button's
 * own label carries the meaning.
 * @param {string} name - key in ICONS
 * @returns {string} svg markup
 */
function icon(name) {
    return '<svg class="' + CONTROLS_CLASS + '__icon" viewBox="0 0 14 14" aria-hidden="true" focusable="false">'
        + ICONS[name] + '</svg>';
}

/**
 * The labels are authored in the template so they stay translatable; fall back to
 * English rather than shipping a button with no accessible name.
 * @param {jQuery} $region - the banner region element
 * @returns {Object} label lookup
 */
function readLabels($region) {
    return {
        group: normalize($region.data('controlsLabel')) || DEFAULT_LABELS.group,
        pause: normalize($region.data('pauseLabel')) || DEFAULT_LABELS.pause,
        play: normalize($region.data('playLabel')) || DEFAULT_LABELS.play,
        previous: normalize($region.data('prevLabel')) || DEFAULT_LABELS.previous,
        next: normalize($region.data('nextLabel')) || DEFAULT_LABELS.next
    };
}

/**
 * tiny-slider gives its container an id of its own, but only once it has wrapped
 * the markup; make sure there is one to point `aria-controls` at either way.
 * @param {jQuery} $track - the tiny-slider container
 * @returns {string} the track's id
 */
function ensureTrackId($track) {
    var id = $track.attr('id');
    if (!id) {
        trackIdSeed += 1;
        id = 'banner-carousel-track-' + trackIdSeed;
        $track.attr('id', id);
    }
    return id;
}

/**
 * Single owner of "should the carousel be rotating right now".
 *
 * Two independent things stop the rotation and they must not undo each other: the
 * shopper pressing Pause (sticky until they press Play) and the transient hold
 * while they are reading or tabbing through the banner. Rotation resumes only when
 * neither applies, so tabbing out of a banner the shopper paused never restarts it.
 *
 * @param {Object} slider - the tiny-slider instance
 * @returns {Object} playback state API
 */
function createPlayback(slider) {
    var userPaused = false;
    var held = false;
    var subscribers = [];

    /**
     * Push the combined state down to tiny-slider.
     * @returns {void}
     */
    function apply() {
        if (userPaused || held) {
            slider.pause();
        } else {
            slider.play();
        }
    }

    /**
     * Tell the UI about a change the shopper made, so the button can relabel.
     * @returns {void}
     */
    function publish() {
        for (var i = 0; i < subscribers.length; i += 1) {
            subscribers[i](userPaused);
        }
    }

    return {
        isUserPaused: function () {
            return userPaused;
        },
        setUserPaused: function (value) {
            if (userPaused === value) {
                return;
            }
            userPaused = value;
            apply();
            publish();
        },
        setHeld: function (value) {
            if (held === value) {
                return;
            }
            held = value;
            apply();
        },
        onUserPauseChange: function (callback) {
            subscribers.push(callback);
            callback(userPaused);
        }
    };
}

/**
 * Build one control button.
 * @param {string} modifier - class suffix identifying the control
 * @param {string} label - accessible name
 * @param {string} iconName - key in ICONS
 * @param {string} controls - id of the element the button operates
 * @returns {jQuery} the button
 */
function controlButton(modifier, label, iconName, controls) {
    return $('<button>', {
        type: 'button',
        'class': BTN_CLASS + ' ' + BTN_CLASS + '--' + modifier,
        'aria-label': label,
        'aria-controls': controls
    }).html(icon(iconName));
}

/**
 * Give the banner the pause/play control WCAG 2.2.2 requires, plus the previous and
 * next controls that let a keyboard user reach a slide the rotation has moved past.
 *
 * Injected from script rather than authored in the template because the slides come
 * from Business Manager content assets: the template cannot know whether there is
 * more than one of them. A banner that never moves - a single slide, so tiny-slider
 * freezes - gets no controls, because there is no motion to control and an inert
 * play button is worse than none.
 *
 * Previous/next also latch the pause on. Someone stepping through the slides has
 * taken over the pacing, and handing it back to a timer mid-read is the exact
 * failure 2.2.2 is about.
 *
 * @param {Object} slider - the tiny-slider instance
 * @param {jQuery} $region - the labelled banner region the controls are added to
 * @param {jQuery} $track - the tiny-slider container
 * @param {Object} playback - the playback state API
 * @returns {jQuery|null} the control group, or null when the banner does not rotate
 */
function buildControls(slider, $region, $track, playback) {
    var info = slider.getInfo();

    if (!$region.length || info.slideCount < 2 || $region.find('.' + CONTROLS_CLASS).length) {
        return null;
    }

    var labels = readLabels($region);
    var trackId = ensureTrackId($track);

    var $prev = controlButton('prev', labels.previous, 'previous', trackId);
    var $toggle = controlButton('toggle', labels.pause, 'pause', trackId);
    var $next = controlButton('next', labels.next, 'next', trackId);

    var $group = $('<div>', {
        'class': CONTROLS_CLASS,
        role: 'group',
        'aria-label': labels.group
    }).append($prev, $toggle, $next);

    $toggle.on('click', function () {
        playback.setUserPaused(!playback.isUserPaused());
    });

    $prev.on('click', function () {
        playback.setUserPaused(true);
        slider.goTo('prev');
    });

    $next.on('click', function () {
        playback.setUserPaused(true);
        slider.goTo('next');
    });

    // The accessible name carries the state, rather than aria-pressed on a fixed
    // name: it is the one form every screen reader announces the same way, and it
    // matches the APG carousel pattern the audit is measured against.
    playback.onUserPauseChange(function (paused) {
        $toggle.attr('aria-label', paused ? labels.play : labels.pause);
        $toggle.html(icon(paused ? 'play' : 'pause'));

        // Silent while the carousel drives itself - announcing a slide the shopper
        // did not ask for is the interruption 2.2.2 exists to stop. Once they are in
        // control, previous/next must say what they landed on.
        $track.attr('aria-live', paused ? 'polite' : 'off');
    });

    $region.addClass(CONTROLS_FLAG_CLASS).append($group);

    return $group;
}

/**
 * Honour an operating-system request for less motion by starting the banner paused.
 * The shopper can still press Play; nothing is hidden from them.
 * @param {Object} playback - the playback state API
 * @returns {void}
 */
function applyReducedMotionPreference(playback) {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        playback.setUserPaused(true);
    }
}

/**
 * Hold the carousel still while the shopper is engaged with it - focus anywhere in
 * the banner, or the pointer over the sliding content - and let it run again once
 * they move on. A keyboard user needs the link to stop moving long enough to reach
 * and activate it; a mouse user reading the message needs the same.
 *
 * Focus and hover have different scopes. Focus covers the whole banner, controls
 * included, so the rotation cannot move a slide out from under someone who has
 * tabbed to Next and not pressed it yet. Hover stays on the slider track, because
 * the banner bar is full width and crossing it is something that happens on the way
 * to the nav.
 *
 * The hold is transient: it is handed to the playback owner, which keeps it apart
 * from a pause the shopper asked for, so releasing it never restarts a carousel
 * they stopped themselves.
 *
 * @param {jQuery} $focusScope - element whose focus holds the rotation
 * @param {jQuery} $hoverScope - element whose hover holds the rotation
 * @param {Object} playback - the playback state API
 */
function bindEngagementPause($focusScope, $hoverScope, playback) {
    var hasFocus = false;
    var hasPointer = false;

    /**
     * Fold both engagement signals into the single transient hold.
     * @returns {void}
     */
    function sync() {
        playback.setHeld(hasFocus || hasPointer);
    }

    $focusScope.on('focusin', function () {
        hasFocus = true;
        sync();
    });

    $focusScope.on('focusout', function (event) {
        // focusout also fires while moving between controls in the same banner.
        var movedTo = event.relatedTarget;
        if (movedTo && (movedTo === $focusScope[0] || $.contains($focusScope[0], movedTo))) {
            return;
        }
        hasFocus = false;
        sync();
    });

    // Only where hovering is a real gesture: on touch, mouseenter can fire without
    // a matching mouseleave, which would leave the carousel held for good.
    if (!window.matchMedia || window.matchMedia('(hover: hover)').matches) {
        $hoverScope.on('mouseenter', function () {
            hasPointer = true;
            sync();
        });

        $hoverScope.on('mouseleave', function () {
            hasPointer = false;
            sync();
        });
    }
}

/**
 * Silence tiny-slider's injected "slide x of y" live region. The banner announces
 * itself through the track's own aria-live, which only speaks while the shopper is
 * driving it; leaving both on would say every move twice.
 * @param {jQuery} $track - the tiny-slider container
 */
function quietSliderLiveRegion($track) {
    $track.closest('.tns-outer').find('.tns-liveregion').attr('aria-live', 'off');
}

/**
 * Apply the banner accessibility layer to an initialised tiny-slider.
 * @param {Object} slider - the tiny-slider instance returned by tns()
 * @param {Object} [options] - optional settings
 * @param {jQuery} [options.$hoverScope] - element to watch for hover (defaults to the track)
 */
function init(slider, options) {
    if (!slider || !slider.getInfo) {
        return;
    }

    var settings = options || {};
    var $track = $(slider.getInfo().container);
    if (!$track.length) {
        return;
    }

    // The region carries the labels and is the natural home for the controls, but
    // never assume it is there: fall back to the track so a banner whose markup has
    // drifted still gets everything that does not depend on it.
    var $region = $track.closest('[data-a11y-connector]');
    var $hoverScope = settings.$hoverScope && settings.$hoverScope.length ? settings.$hoverScope : $track;
    var $focusScope = $region.length ? $region : $track;
    // The connector word is authored in the template so it stays translatable.
    var connector = normalize($region.data('a11yConnector')) || DEFAULT_CONNECTOR;
    var playback = createPlayback(slider);

    decorateControls($track, connector);
    syncFocusability(slider, $track);
    quietSliderLiveRegion($track);
    bindKeyboardActivation($track);

    // Before the controls are built, so the toggle renders as Play when the shopper
    // has asked their system for reduced motion.
    applyReducedMotionPreference(playback);
    buildControls(slider, $region, $track, playback);
    bindEngagementPause($focusScope, $hoverScope, playback);

    slider.events.on('indexChanged', function () {
        syncFocusability(slider, $track);
    });
    slider.events.on('transitionEnd', function () {
        syncFocusability(slider, $track);
    });
}

module.exports = {
    init: init
};
